import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileText, KeyRound, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("This link is missing its reset token.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <Card className="w-full max-w-md p-8" data-testid="reset-missing-token">
          <div className="flex items-center gap-3 mb-4 text-red-600">
            <AlertCircle size={22} />
            <h1 className="text-lg font-bold">Invalid reset link</h1>
          </div>
          <p className="text-sm text-zinc-500 mb-5">
            This link is missing its reset token or has already been used. Request a new one to continue.
          </p>
          <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 h-10">
            <Link to="/forgot-password" data-testid="link-request-again">Request a new link</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <Card className="w-full max-w-md p-8" data-testid="reset-password-card">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-blue-600 text-white flex items-center justify-center">
            <FileText size={18} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">Set a new password</div>
            <h1 className="text-xl font-bold tracking-tight">Reset password</h1>
          </div>
        </div>

        {done ? (
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-4" data-testid="reset-success">
            <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-emerald-900">
              Password updated. Redirecting you to sign in…
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">New password</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 mt-1 text-sm"
                placeholder="At least 6 characters"
                data-testid="reset-password"
                required
                minLength={6}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Confirm new password</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-10 mt-1 text-sm"
                placeholder="Re-enter new password"
                data-testid="reset-confirm"
                required
                minLength={6}
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 h-10"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : (
                <KeyRound size={16} className="mr-2" />
              )}
              {loading ? "Updating…" : "Set new password"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
